<template>
    <div class="yield-create-page">
        <v-container fluid class="yield-create-container">
            <div class="breadcrumb-line">
                {{ $t("reports.reportsBreadcrumb") }} <span>›</span> {{ $t("reports.yieldPrediction") }}
            </div>
            <div class="create-header">
                <div class="title-row">
                    <h2>{{ $t("reports.yieldPrediction") }}</h2>
                    <v-btn small rounded color="primary" class="guide-button" @click="viewGuide">
                        <v-icon left small>mdi-help-circle-outline</v-icon>
                        {{ $t("reports.aiReportGuide") }}
                    </v-btn>
                </div>
                <v-spacer />
                <div class="header-actions">
                    <v-btn outlined color="primary" @click="handleCancel">
                        {{ $t("reports.cancel") }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        dark
                        :loading="generatingReport"
                        :disabled="!canGenerateReport"
                        @click="handleGenerateReport"
                    >
                        {{ $t("reports.generateReport") }}
                    </v-btn>
                </div>
            </div>

            <v-row class="selection-workspace no-gutters">
                <!-- Left Section: Map -->
                <v-col cols="12" md="8" class="map-column">
                    <v-card elevation="0" class="map-card">
                        <div class="map-heading">
                            <div>
                                <h3>{{ $t("reports.selectFarmGeofence") }}</h3>
                                <p>{{ $t("reports.selectFarmGeofenceDescription") }}</p>
                            </div>
                        </div>

                        <geofence-map
                            :source="source"
                            :zone="selectedZone"
                            :geojson-coordinates="geojsonCoords"
                            :radius-meters="circleRadius"
                            @update:geometry="onDrawGeometry"
                        />
                    </v-card>
                </v-col>

                <!-- Right Section: Form Inputs -->
                <v-col cols="12" md="4" class="form-column">
                    <v-card elevation="0" class="form-card">
                        <v-btn-toggle v-model="source" mandatory dense class="flex-wrap mb-4 d-flex source-toggle">
                            <v-btn value="farm_zone">{{ $t("reports.sourceFarmZone") }}</v-btn>
                            <v-btn value="polygon">{{ $t("reports.sourcePolygon") }}</v-btn>
                            <v-btn value="circle">{{ $t("reports.sourceCircle") }}</v-btn>
                            <v-btn value="geojson">{{ $t("reports.sourceGeojson") }}</v-btn>
                        </v-btn-toggle>

                        <!-- Farm / Zone -->
                        <div v-if="source === 'farm_zone'" class="mb-4">
                            <label class="mb-2 d-block font-weight-medium">{{ $t("reports.selectFarmZone") }}</label>
                            <farm-zone-select v-model="selectedZone" :label="$t('reports.selectFarmZone')" />
                        </div>

                        <!-- Draw polygon: instructions -->
                        <div v-else-if="source === 'polygon'" class="mb-4">
                            <v-alert dense text type="info" class="mb-0">
                                {{ $t("reports.drawPolygon") }} — {{ $t("reports.drawPolygonHelp") }}
                            </v-alert>
                        </div>

                        <!-- Draw circle: instructions + radius -->
                        <div v-else-if="source === 'circle'" class="mb-4">
                            <v-alert dense text type="info" class="mb-3">
                                {{ $t("reports.drawCircle") }} — {{ $t("reports.drawCircleHelp") }}
                            </v-alert>
                            <v-text-field
                                v-model.number="circleRadius"
                                :label="$t('reports.radiusM')"
                                type="number"
                                min="1"
                                suffix="m"
                                outlined
                                dense
                                hide-details
                            />
                        </div>

                        <!-- Upload GeoJSON -->
                        <div v-else-if="source === 'geojson'" class="upload-block">
                            <label>{{ $t("reports.uploadGeojsonFile") }}</label>
                            <p>{{ $t("reports.uploadGeojsonFileDescription") }}</p>
                            <v-file-input
                                accept=".geojson,.json"
                                :label="$t('reports.selectAFile')"
                                prepend-icon="mdi-upload"
                                outlined
                                dense
                                hide-details
                                @change="onGeojsonUpload"
                            />
                        </div>

                        <!-- Select Crop -->
                        <div class="mb-4">
                            <label class="mb-2 d-block font-weight-medium">
                                {{ $t("reports.selectCrop") }}
                            </label>
                            <v-autocomplete
                                v-model="selectedCrop"
                                :items="crops"
                                item-text="name"
                                item-value="id"
                                :placeholder="$t('reports.select')"
                                :no-data-text="$t('noDataAvailable')"
                                outlined
                                dense
                                clearable
                                hide-details
                                :loading="loadingCrops"
                                :search-input.sync="cropSearch"
                            ></v-autocomplete>
                        </div>

                        <!-- Show Advanced -->
                        <!-- <div class="advanced-toggle">
                            <a
                                href="#"
                                @click.prevent="showAdvanced = !showAdvanced"
                            >
                                {{ showAdvanced ? $t("reports.hideAdvanced") : $t("reports.showAdvanced") }}
                            </a>
                        </div> -->

                        <!-- Advanced Options -->
                        <v-expand-transition>
                            <div v-if="showAdvanced" class="mb-4">
                                <v-divider class="mb-6"></v-divider>

                                <h3 class="mb-4">{{ $t("reports.showAdvancedVariation") }}</h3>

                                <!-- Advanced: Select Crop -->
                                <div class="mb-4">
                                    <label class="mb-2 d-block font-weight-medium">
                                        {{ $t("reports.selectCrop") }}
                                    </label>
                                    <v-autocomplete
                                        v-model="advancedCrop"
                                        :items="crops"
                                        item-text="name"
                                        item-value="id"
                                        :placeholder="$t('reports.select')"
                                        :no-data-text="$t('noDataAvailable')"
                                        outlined
                                        dense
                                        clearable
                                        :loading="loadingCrops"
                                        :search-input.sync="advancedCropSearch"
                                    ></v-autocomplete>
                                </div>

                                <!-- Advanced: Upload historical yield data -->
                                <div class="mb-4">
                                    <p class="mb-3">
                                        {{ $t("reports.uploadHistoricalYieldDataDescription") }}
                                    </p>

                                    <v-row>
                                        <v-col cols="12" md="4" class="mb-2">
                                            <v-btn
                                                block
                                                color="primary"
                                                @click="handleShowAdvancedInstructions"
                                            >
                                                {{ $t("reports.showInstructions") }}
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="12" md="4" class="mb-2">
                                            <v-btn
                                                block
                                                color="primary"
                                                @click="handleDownloadAdvancedTemplate"
                                            >
                                                {{ $t("reports.downloadTemplate") }}
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="12" md="4" class="mb-2">
                                            <v-btn
                                                block
                                                color="primary"
                                                dark
                                                @click="triggerAdvancedFileSelect"
                                            >
                                                {{ $t("reports.chooseFile") }}
                                            </v-btn>
                                            <!-- hidden file input to mimic design -->
                                            <input
                                                ref="advancedFileInput"
                                                type="file"
                                                accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                                style="display:none"
                                                @change="handleAdvancedFileChange"
                                            />
                                        </v-col>
                                    </v-row>

                                    <div class="mt-1 text-caption grey--text">
                                        {{ advancedFileName || $t("reports.noFileSelected") }}
                                    </div>
                                </div>

                                <div class="mt-4">
                                    <a
                                        href="#"
                                        class="text-decoration-none primary--text font-weight-medium"
                                        @click.prevent="showAdvanced = false"
                                    >
                                        {{ $t("reports.hideAdvanced") }}
                                    </a>
                                </div>
                            </div>
                        </v-expand-transition>
                    </v-card>
                </v-col>
            </v-row>

            <estimated-compute-cost
                report-type="yield_prediction"
                :geofence="geofencePayload"
            />
        </v-container>

        <yield-prediction-guide-dialog v-model="showGuide" />
    </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import YieldPredictionService from '@/_services/YieldPredictionService'
import FarmZoneSelect from '../crop-progression/components/FarmZoneSelect.vue';
import GeofenceMap from '../crop-progression/components/GeofenceMap.vue';
import EstimatedComputeCost from '../shared/EstimatedComputeCost.vue';
import YieldPredictionGuideDialog from './components/YieldPredictionGuideDialog.vue';
import { circleToPolygon } from '@/utils/geoShapes';

const DEFAULT_CIRCLE_RADIUS_M = 20; // meters

export default {
    components: { FarmZoneSelect, GeofenceMap, EstimatedComputeCost, YieldPredictionGuideDialog },
    mixins: [loadingMixin],
    computed: {
        // Unified coordinate ring for whichever source is active, used both to
        // build the API payload and to know whether a valid geometry exists.
        zoneRing() {
            if (!this.selectedZone) return [];
            const rawCoordinates =
                this.selectedZone.geofence_coordinates || this.selectedZone.coordinates || [];
            const path = (Array.isArray(rawCoordinates) ? rawCoordinates : [])
                .map((point) => ({
                    lat: Number(point.lat ?? point.latitude),
                    lng: Number(point.lng ?? point.log ?? point.longitude),
                }))
                .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
            if (path.length >= 3) return path;

            const lat = Number(this.selectedZone.geofenceCenterLat ?? this.selectedZone.lat ?? this.selectedZone.latitude);
            const lng = Number(
                this.selectedZone.geofenceCenterLog ?? this.selectedZone.lng ?? this.selectedZone.log ?? this.selectedZone.longitude
            );
            return Number.isFinite(lat) && Number.isFinite(lng) ? [{ lat, lng }] : [];
        },
        ring() {
            switch (this.source) {
                case 'farm_zone':
                    return this.zoneRing;
                case 'polygon':
                    return this.drawnGeometry && this.drawnGeometry.shape === 'polygon'
                        ? this.drawnGeometry.coordinates
                        : [];
                case 'circle':
                    return this.drawnGeometry && this.drawnGeometry.shape === 'circle'
                        ? circleToPolygon(
                              this.drawnGeometry.latitude,
                              this.drawnGeometry.longitude,
                              this.drawnGeometry.radius * 1000
                          )
                        : [];
                case 'geojson':
                    return this.geojsonCoords;
                default:
                    return [];
            }
        },
        hasGeometry() {
            return this.ring.length > 0;
        },
        canGenerateReport() {
            return Boolean(this.selectedCrop && this.hasGeometry);
        },
        geofencePayload() {
            switch (this.source) {
                case 'farm_zone':
                    return this.selectedZone
                        ? {
                              geofenceType: 'farm_zone',
                              geofenceId: this.selectedZone.geofenceId,
                              farmId: this.selectedZone.farmId,
                          }
                        : null;
                case 'polygon':
                    return this.drawnGeometry && this.drawnGeometry.shape === 'polygon'
                        ? { geofenceType: 'polygon', coordinates: this.drawnGeometry.coordinates }
                        : null;
                case 'circle':
                    return this.drawnGeometry && this.drawnGeometry.shape === 'circle'
                        ? {
                              geofenceType: 'circle',
                              latitude: this.drawnGeometry.latitude,
                              longitude: this.drawnGeometry.longitude,
                              radius: this.drawnGeometry.radius,
                          }
                        : null;
                case 'geojson':
                    return this.geojsonCoords.length >= 3
                        ? { geofenceType: 'geojson', coordinates: this.geojsonCoords }
                        : null;
                default:
                    return null;
            }
        },
    },
    data() {
        return {
            publicPath: process.env.BASE_URL || '/',
            source: 'farm_zone', // 'farm_zone' | 'polygon' | 'circle' | 'geojson'
            selectedZone: null,
            circleRadius: DEFAULT_CIRCLE_RADIUS_M,
            drawnGeometry: null,
            geojsonCoords: [],
            selectedCrop: null,
            cropSearch: null,
            advancedCrop: null,
            advancedCropSearch: null,
            advancedFileName: '',
            showAdvanced: false,
            generatingReport: false,
            loadingCrops: false,
            crops: [],
            breadcrumbs: [
                {
                    text: this.$t("dashboard") || "Dashboard",
                    disabled: false,
                    to: "/",
                },
                {
                    text: this.$t("reports.yieldPrediction") || "Yield Prediction",
                    disabled: true,
                    to: "",
                },
            ],
            showGuide: false,
        };
    },
    watch: {
        source() {
            this.drawnGeometry = null;
            if (this.source !== 'geojson') this.geojsonCoords = [];
            if (this.source !== 'farm_zone') this.selectedZone = null;
            if (this.source === 'circle') this.circleRadius = DEFAULT_CIRCLE_RADIUS_M;
        },
    },
    async mounted() {
        await this.getCrops();
    },
    methods: {
        onDrawGeometry(geom) {
            this.drawnGeometry = geom;
            if (geom && geom.shape === 'circle' && geom.radius != null) {
                const meters = Math.round(geom.radius * 1000);
                if (Math.abs(meters - Number(this.circleRadius)) >= 1) {
                    this.circleRadius = meters;
                }
            }
        },
        async onGeojsonUpload(file) {
            if (!file) {
                this.geojsonCoords = [];
                return;
            }
            const text = await file.text().catch(() => null);
            if (!text) return;
            let parsed;
            try {
                parsed = JSON.parse(text);
            } catch (error) {
                this.$notify({
                    title: this.$t('reports.invalidGeojsonFile'),
                    text: this.$t('reports.invalidGeojsonFileDescription'),
                    type: 'error',
                });
                return;
            }
            const coords = this.extractPolygonFromGeojson(parsed);
            if (coords.length < 3) {
                this.$notify({
                    title: this.$t('reports.invalidGeojsonFile'),
                    text: this.$t('reports.invalidGeojsonFileDescription'),
                    type: 'error',
                });
                return;
            }
            this.geojsonCoords = coords;
        },
        extractPolygonFromGeojson(geojson) {
            const ringToLatLng = (ring) => ring.map((c) => ({ lat: c[1], lng: c[0] }));
            try {
                if (geojson.type === 'FeatureCollection' && geojson.features && geojson.features.length) {
                    return this.extractPolygonFromGeojson(geojson.features[0]);
                }
                const geom = geojson.type === 'Feature' ? geojson.geometry : geojson;
                if (!geom) return [];
                if (geom.type === 'Polygon') return ringToLatLng(geom.coordinates[0]);
                if (geom.type === 'MultiPolygon') return ringToLatLng(geom.coordinates[0][0]);
            } catch (error) {
                return [];
            }
            return [];
        },
        async getCrops() {
            this.loadingCrops = true;
            try {
                await YieldPredictionService.getCropList().then(res => {
                    if (res.data && res.data.crops && Array.isArray(res.data.crops)) {
                        this.crops = res.data.crops.map(crop => ({
                            id: crop,
                            name: crop
                        }));
                    } else {
                        // Fallback to common crops if API doesn't return expected format
                        this.crops = [
                            { id: 'Coffee', name: 'Coffee' },
                            { id: 'Cocoa', name: 'Cocoa' },
                            { id: 'Rice', name: 'Rice' },
                            { id: 'Maize (corn)', name: 'Maize (corn)' },
                            { id: 'Wheat', name: 'Wheat' },
                            { id: 'Soybean', name: 'Soybean' },
                        ];
                    }
                    this.loadingCrops = false;
                }).catch(err => {
                    console.error('Error fetching crops:', err);
                    // Fallback to common crops on error
                    this.crops = [
                        { id: 'Coffee', name: 'Coffee' },
                        { id: 'Cocoa', name: 'Cocoa' },
                        { id: 'Rice', name: 'Rice' },
                        { id: 'Maize (corn)', name: 'Maize (corn)' },
                        { id: 'Wheat', name: 'Wheat' },
                        { id: 'Soybean', name: 'Soybean' },
                    ];
                    this.loadingCrops = false;
                });
            } catch (error) {
                console.error('Error fetching crops:', error);
                this.loadingCrops = false;
            }
        },
        viewGuide() {
            this.showGuide = true;
        },
        closeGuide() {
            this.showGuide = false;
        },
        handleCancel() {
            this.$router.go(-1);
        },
        async handleGenerateReport() {
            if (!this.selectedCrop) {
                this.$notify({
                    title: this.$t('reports.validationError'),
                    text: this.$t('reports.pleaseSelectCrop'),
                    type: 'error',
                });
                return;
            }

            if (!this.hasGeometry) {
                this.$notify({
                    title: this.$t('reports.validationError'),
                    text: this.$t('reports.pleaseDrawGeofence'),
                    type: 'error',
                });
                return;
            }

            this.generatingReport = true;
            try {
                // External v3 API is point-based; non-point types are reduced to a
                // centroid server-side, so any ring of >=1 points works here.
                const coordinates = this.ring.map((c) => [c.lng, c.lat]);

                const farmName = this.selectedZone
                    ? this.selectedZone.farmName
                    : null;

                // Format reportDate as DD-MMM-YYYY (e.g., "05-Jan-2026")
                const reportDate = new Date().toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                });

                const payload = {
                    crop_name: this.selectedCrop,
                    type: this.source,
                    coordinates: JSON.stringify(coordinates),
                    farmId: this.selectedZone ? this.selectedZone.farmId : null,
                    geofenceId: this.selectedZone ? this.selectedZone.geofenceId : null,
                    farmName: farmName,
                    zoneName: this.selectedZone
                        ? (this.selectedZone.zoneName || null)
                        : null,
                    reportDate: reportDate,
                    // Omit indices — satellite v3 fails with float('') when indices is sent.
                };

                const response = await YieldPredictionService.createYieldPrediction(payload);

                // Check if response indicates failure (even if HTTP status is 200)
                if (response && response.data && response.data.success === false) {
                    throw new Error(response.data.message || this.$t("reports.failedToGenerateReport"));
                }

                this.$notify({
                    title: this.$t('success'),
                    text: this.$t('reports.yieldReportGeneratedSuccessfully'),
                    type: 'success',
                });

                // Navigate to report view or list only on success
                this.$router.push({ name: 'yieldPredictionList' });
            } catch (error) {
                console.error('Error generating report:', error);

                // Extract error message from various possible response structures
                let errorMessage = this.$t('reports.failedToGenerateReport');

                if (error.response) {
                    // Axios error response
                    if (error.response.data) {
                        if (typeof error.response.data === 'string') {
                            errorMessage = error.response.data;
                        } else if (error.response.data.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.response.data.data && error.response.data.data.message) {
                            errorMessage = error.response.data.data.message;
                        }
                    }
                } else if (error.message) {
                    // Standard Error object
                    errorMessage = error.message;
                }

                this.$notify({
                    title: this.$t('error') || 'Error',
                    text: errorMessage,
                    type: 'error',
                });

                // Do NOT redirect on error - stay on the create page
            } finally {
                this.generatingReport = false;
            }
        },
        handleShowAdvancedInstructions() {
            window.open('https://docs.google.com/document/d/1YRQB9L12ycTg3di504ty1oku64hII4Xy_Qn2dcc99bM/edit?tab=t.0', '_blank');
        },
        handleDownloadAdvancedTemplate() {
            // Create a temporary anchor element to trigger download
            const link = document.createElement('a');
            link.href = '/files/yield-template.xlsx';
            link.download = 'yield-template.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        triggerAdvancedFileSelect() {
            if (this.$refs.advancedFileInput) {
                this.$refs.advancedFileInput.click();
            }
        },
        handleAdvancedFileChange(event) {
            const file = event?.target?.files?.[0];
            if (file) {
                this.advancedFileName = file.name;
            } else {
                this.advancedFileName = '';
            }
        }
    },
};
</script>

<style lang="scss" scoped>
.yield-create-page {
    min-height: 100%;
    background: #f5f6f8;
    font-size: 14px;
    color: #1f292e;
}

.yield-create-container {
    padding: 24px 32px 40px;
}

.breadcrumb-line {
    margin-bottom: 12px;
    color: #7c858d;
    font-size: 13px;

    span {
        margin: 0 4px;
    }
}

.create-header,
.title-row,
.header-actions,
.map-heading {
    display: flex;
    align-items: center;
}

.create-header {
    margin-bottom: 20px;
    gap: 16px;
}

.title-row,
.header-actions {
    gap: 12px;
}

.title-row h2 {
    color: #182126;
    font-size: 24px;
}

.guide-button {
    box-shadow: none;
    font-size: 13px;
}

.header-actions .v-btn {
    min-width: 128px;
    height: 40px;
    border-radius: 3px;
    box-shadow: none;
    font-size: 14px;
    font-weight: 600;
}

.selection-workspace {
    overflow: hidden;
    background: #fff;
}

.map-column {
    border-right: 1px solid #e3e7e9;
}

.map-card,
.form-card {
    padding: 18px 16px;
}

.map-heading {
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 10px;

    h3 {
        margin-bottom: 4px;
        color: #1f292e;
        font-size: 18px;
    }

    p {
        max-width: 520px;
        margin: 0;
        color: #4e595f;
        font-size: 13px;
        line-height: 1.45;
    }
}

.form-card {
    min-height: 100%;

    label {
        display: block;
        margin-bottom: 7px !important;
        color: #25313a;
        font-size: 13px;
        font-weight: 600;
    }

    ::v-deep .v-input__slot {
        min-height: 40px !important;
        border-radius: 2px;
    }

    ::v-deep .v-text-field input,
    ::v-deep .v-select__selection,
    ::v-deep .v-select__selections,
    ::v-deep .v-label {
        font-size: 14px !important;
    }
}

.source-toggle {
    width: 100%;

    ::v-deep .v-btn {
        flex: 1;
    }
}

.upload-block {
    padding: 0 0 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid #dce1e4;

    label {
        color: #18a56d;
        font-size: 14px;
    }

    p {
        margin: 0 0 12px;
        color: #4f5961;
        font-size: 13px;
        line-height: 1.45;
    }
}

.advanced-toggle {
    margin-top: -2px;
    margin-bottom: 2px;

    a {
        color: #18a56d;
        font-size: 13px;
        font-weight: 600;
        text-decoration: none;
        text-transform: uppercase;
    }
}

::v-deep .v-alert__content {
    width: 100%;
}

@media (max-width: 960px) {
    .create-header,
    .map-heading {
        align-items: flex-start;
        flex-direction: column;
    }

    .create-header .v-spacer {
        display: none;
    }

    .map-column {
        border-right: 0;
        border-bottom: 1px solid #e3e7e9;
    }
}
</style>
